"use client";
import { useCompositeItem } from "../composite/composite-item.js";
import { useCompositeHover } from "../composite/composite-hover.js";
import { useMenubarScopedContext } from "../menubar/menubar-context.js";
import { MenuListHiddenContext, useMenuScopedContext } from "./menu-context.js";
import { createElement, createHook, forwardRef, memo, useBooleanEvent, useEvent } from "@ariakit/react-utils";
import { getDocument, getPopupItemRole, hasFocusWithin, invariant, isDownloading, isOpeningInNewTab } from "@ariakit/utils";
import { useContext } from "react";
import { useStoreState } from "@ariakit/react-store";
//#region src/menu/menu-item.tsx
const TagName = "div";
function menuHasFocus(baseElement, items, currentTarget) {
	if (!baseElement) return false;
	if (hasFocusWithin(baseElement)) return true;
	const expandedMenuId = (items?.find((item) => {
		if (item.element === currentTarget) return false;
		return item.element?.getAttribute("aria-expanded") === "true";
	}))?.element?.getAttribute("aria-controls");
	if (!expandedMenuId) return false;
	const expandedMenu = getDocument(baseElement).getElementById(expandedMenuId);
	if (!expandedMenu) return false;
	if (hasFocusWithin(expandedMenu)) return true;
	return !!expandedMenu.querySelector("[role=menuitem][aria-expanded=true]");
}
/**
* Returns props to create a `MenuItem` component.
* @see https://ariakit.com/components/menu
* @example
* ```jsx
* const store = useMenuStore();
* const undo = useMenuItem({ store });
* const redo = useMenuItem({ store });
* <MenuButton store={store}>Edit</MenuButton>
* <Menu store={store}>
*   <Role {...undo}>Undo</Role>
*   <Role {...redo}>Redo</Role>
* </Menu>
* ```
*/
const useMenuItem = createHook(function useMenuItem({ store, hideOnClick = true, preventScrollOnKeyDown = true, focusOnHover, blurOnHoverEnd, ...props }) {
	const menuContext = useMenuScopedContext(true);
	const menubarContext = useMenubarScopedContext();
	store = store || menuContext || menubarContext;
	invariant(store, process.env.NODE_ENV !== "production" && "MenuItem must be wrapped in a MenuList, Menu or Menubar component");
	const onClickProp = props.onClick;
	const hideOnClickProp = useBooleanEvent(hideOnClick);
	const hideMenu = "hideAll" in store ? store.hideAll : void 0;
	const isWithinMenu = !!hideMenu;
	const onClick = useEvent((event) => {
		onClickProp?.(event);
		if (event.defaultPrevented) return;
		if (isDownloading(event)) return;
		if (isOpeningInNewTab(event)) return;
		if (!hideMenu) return;
		const popupType = event.currentTarget.getAttribute("aria-haspopup");
		if (popupType && popupType !== "false") return;
		if (!hideOnClickProp(event)) return;
		hideMenu();
	});
	const contentElement = useStoreState(store, (state) => "contentElement" in state ? state.contentElement : null);
	const menuListHidden = useContext(MenuListHiddenContext);
	props = {
		role: getPopupItemRole(contentElement, "menuitem"),
		...props,
		onClick
	};
	props = useCompositeItem({
		store,
		preventScrollOnKeyDown,
		...props,
		shouldRegisterItem: menuListHidden ? false : props.shouldRegisterItem
	});
	props = useCompositeHover({
		store,
		...props,
		focusOnHover(event) {
			const getFocusOnHover = () => {
				if (typeof focusOnHover === "function") return focusOnHover(event);
				if (focusOnHover != null) return focusOnHover;
				return true;
			};
			if (!store) return false;
			if (!getFocusOnHover()) return false;
			const { baseElement, items } = store.getState();
			if (isWithinMenu) {
				if (event.currentTarget.hasAttribute("aria-expanded")) event.currentTarget.focus();
				return true;
			}
			if (menuHasFocus(baseElement, items, event.currentTarget)) {
				event.currentTarget.focus();
				return true;
			}
			return false;
		},
		blurOnHoverEnd(event) {
			if (typeof blurOnHoverEnd === "function") return blurOnHoverEnd(event);
			if (blurOnHoverEnd != null) return blurOnHoverEnd;
			return isWithinMenu;
		}
	});
	return props;
});
/**
* Renders a menu item inside
* [`MenuList`](https://ariakit.com/reference/menu-list) or
* [`Menu`](https://ariakit.com/reference/menu)
* components.
* @see https://ariakit.com/components/menu
* @example
* ```jsx {4-5}
* <MenuProvider>
*   <MenuButton>Edit</MenuButton>
*   <Menu>
*     <MenuItem>Undo</MenuItem>
*     <MenuItem>Redo</MenuItem>
*   </Menu>
* </MenuProvider>
* ```
*/
const MenuItem = memo(forwardRef(function MenuItem(props) {
	return createElement(TagName, useMenuItem(props));
}));
//#endregion
export { MenuItem, useMenuItem };

//# sourceMappingURL=menu-item.js.map